import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { Zap, Loader2, ArrowRight } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("owner@meridian.ops");
  const [password, setPassword] = useState("Owner!2026");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-[46%] bg-ink text-white flex-col justify-between p-12 grid-backdrop relative">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-brand flex items-center justify-center">
            <Zap className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="font-heading font-extrabold text-lg tracking-tight">MERIDIAN CONTROL TOWER</div>
        </div>
        <div>
          <h1 className="font-heading text-5xl font-extrabold tracking-tighter leading-[1.05]">
            Evidence-backed<br />autonomy for<br />every directive.
          </h1>
          <p className="text-white/60 mt-6 max-w-md text-[15px] leading-relaxed">
            Agents work autonomously across every trade and company. Every action is logged,
            every deliverable validated, and nothing reaches a client without your sign-off.
          </p>
          <div className="flex gap-6 mt-10">
            {[["4", "Memory layers"], ["14", "Domain agents"], ["1", "Approval flow"]].map(([n, l]) => (
              <div key={l}>
                <div className="font-heading text-3xl font-bold text-brand" style={{ color: "#7CA0FF" }}>{n}</div>
                <div className="text-[11px] uppercase tracking-wider text-white/40 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="text-[11px] text-white/30 font-mono">Internal operations platform · v1</div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-ink">Sign in</h2>
            <p className="text-sm text-ink-muted mt-1">Access the control tower.</p>
          </div>

          <label className="text-[11px] font-semibold tracking-[0.1em] uppercase text-ink-muted mb-1.5 block">Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
            className="w-full border border-border px-3 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand mb-4"
            placeholder="you@company.com" required
          />
          <label className="text-[11px] font-semibold tracking-[0.1em] uppercase text-ink-muted mb-1.5 block">Password</label>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
            className="w-full border border-border px-3 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand mb-4"
            placeholder="••••••••" required
          />

          {error && <div className="mb-4 px-3 py-2 bg-st-critical/10 border border-st-critical text-sm text-st-critical" data-testid="login-error">{error}</div>}

          <button
            type="submit" disabled={loading} data-testid="login-submit"
            className="w-full flex items-center justify-center gap-2 bg-brand text-white py-2.5 text-sm font-semibold hover:bg-brand-dark disabled:opacity-60 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
          </button>

          <div className="mt-6 border border-dashed border-border bg-surface-muted p-3 text-xs text-ink-muted">
            <div className="font-semibold text-ink mb-1">Demo accounts</div>
            <div className="font-mono">owner@meridian.ops · Owner!2026</div>
            <div className="font-mono text-ink-faint">ops@meridian.ops · Ops!2026</div>
          </div>
        </form>
      </div>
    </div>
  );
}
